import * as d3 from 'd3';
import type {
  RadarData,
  RadarEntry,
  RadarVisualizationOptions,
  Position,
} from '@/types/radar';
import { PositionCalculator } from '@/utils/geometry';
import {
  UI_COLORS,
  getRingColor,
  getRingFillColor,
  getRingGradient,
  RADAR_DIMENSIONS,
  LABEL_POSITIONING,
  LEGEND_CONFIG,
  BLIP_STYLING,
} from '@/config';

/**
 * D3.js-based radar visualization component
 */
export class Radar {
  private svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private data: RadarData;
  private width: number;
  private height: number;
  private radius: number;
  private centerX: number;
  private centerY: number;
  private positionCalculator: PositionCalculator;
  private positions: Map<string, Position>;
  private onBlipClick?: (entry: RadarEntry) => void;
  private onQuadrantClick?: (quadrantId: string) => void;

  constructor(
    container: HTMLElement,
    data: RadarData,
    options: RadarVisualizationOptions = {}
  ) {
    this.data = data;

    // Set dimensions using constants
    this.width = options.width || RADAR_DIMENSIONS.defaultWidth;
    this.height = options.height || RADAR_DIMENSIONS.defaultHeight;
    this.radius = options.radius || Math.min(this.width, this.height) / 2 - RADAR_DIMENSIONS.radiusMargin;
    this.centerX = this.width / 2;
    this.centerY = this.height / 2;

    // Calculate positions
    this.positionCalculator = new PositionCalculator(data.config, this.radius);
    this.positions = this.positionCalculator.calculate(data.entries);

    // Initialize SVG
    this.svg = d3
      .select(container)
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height)
      .attr('class', 'tech-radar');
  }

  /**
   * Render the complete radar visualization
   */
  render(): void {
    this.renderGradients();
    this.renderBackground();
    this.renderRings();
    this.renderQuadrantDividers();
    this.renderQuadrantLabels();
    this.renderBlips();
    this.renderLegend();
  }

  /**
   * Render SVG gradients for premium look
   */
  private renderGradients(): void {
    const defs = this.svg.append('defs');

    // Generate ring gradients from centralized configuration
    const sortedRings = [...this.data.config.rings].sort((a, b) => a.order - b.order);
    const ringGradients = sortedRings.map((ring, index) => getRingGradient(ring.id, index));

    ringGradients.forEach((grad) => {
      const gradient = defs.append('linearGradient')
        .attr('id', grad.id)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '100%');

      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', grad.color1)
        .attr('stop-opacity', 0.4);

      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', grad.color2)
        .attr('stop-opacity', 0.6);
    });
  }

  /**
   * Set click handler for blips
   */
  setBlipClickHandler(handler: (entry: RadarEntry) => void): void {
    this.onBlipClick = handler;
  }

  /**
   * Set click handler for quadrant labels
   */
  setQuadrantClickHandler(handler: (quadrantId: string) => void): void {
    this.onQuadrantClick = handler;
  }

  /**
   * Update data and re-render
   */
  update(data: RadarData): void {
    this.data = data;
    // Don't recalculate positions - keep existing positions so blips don't move
    this.svg.selectAll('*').remove();
    this.render();
  }

  /**
   * Render background
   */
  private renderBackground(): void {
    this.svg
      .append('rect')
      .attr('width', this.width)
      .attr('height', this.height)
      .attr('fill', '#ffffff');
  }

  /**
   * Render rings (concentric circles)
   */
  private renderRings(): void {
    const ringGroup = this.svg
      .append('g')
      .attr('class', 'rings')
      .attr('transform', `translate(${this.centerX}, ${this.centerY})`);

    const sortedRings = [...this.data.config.rings].sort((a, b) => a.order - b.order);

    sortedRings.forEach((ring, index) => {
      const ringRadius = (this.radius * (index + 1)) / sortedRings.length;

      // Use centralized color configuration
      const borderColor = getRingColor(ring.id);
      const fillColor = getRingFillColor(ring.id);

      // Ring circle with border and lighter fill
      ringGroup
        .append('circle')
        .attr('r', ringRadius)
        .attr('fill', fillColor)
        .attr('stroke', borderColor)
        .attr('stroke-width', 2.5)
        .attr('opacity', 1);

      // Ring label using constants
      ringGroup
        .append('text')
        .attr('y', -ringRadius + LABEL_POSITIONING.ringLabelYOffset)
        .attr('text-anchor', 'middle')
        .attr('class', 'ring-label')
        .attr('fill', '#333')
        .attr('font-size', `${LABEL_POSITIONING.ringLabelFontSize}px`)
        .attr('font-weight', LABEL_POSITIONING.ringLabelFontWeight)
        .text(ring.name.toUpperCase());
    });
  }

  /**
   * Render quadrant dividers
   */
  private renderQuadrantDividers(): void {
    const dividerGroup = this.svg
      .append('g')
      .attr('class', 'dividers')
      .attr('transform', `translate(${this.centerX}, ${this.centerY})`);

    // Horizontal divider
    dividerGroup
      .append('line')
      .attr('x1', -this.radius)
      .attr('y1', 0)
      .attr('x2', this.radius)
      .attr('y2', 0)
      .attr('stroke', UI_COLORS.divider)
      .attr('stroke-width', 1);

    // Vertical divider
    dividerGroup
      .append('line')
      .attr('x1', 0)
      .attr('y1', -this.radius)
      .attr('x2', 0)
      .attr('y2', this.radius)
      .attr('stroke', UI_COLORS.divider)
      .attr('stroke-width', 1);
  }

  /**
   * Render quadrant labels - ThoughtWorks style at 45-degree angles
   */
  private renderQuadrantLabels(): void {
    const labelGroup = this.svg
      .append('g')
      .attr('class', 'quadrant-labels')
      .attr('transform', `translate(${this.centerX}, ${this.centerY})`);

    // Position labels at 45-degree angles (diagonal) like ThoughtWorks
    const labelDistance = this.radius + LABEL_POSITIONING.quadrantLabelDistance;

    // Calculate positions at 45, 135, 225, 315 degrees
    const labelPositions = [
      {
        order: 0, // Tools (bottom-right quadrant)
        angle: 45,
        anchor: 'start',
        baseline: 'middle'
      },
      {
        order: 1, // Techniques (bottom-left quadrant)
        angle: 135,
        anchor: 'end',
        baseline: 'middle'
      },
      {
        order: 2, // Platforms (top-left quadrant)
        angle: 225,
        anchor: 'end',
        baseline: 'middle'
      },
      {
        order: 3, // Languages & Frameworks (top-right quadrant)
        angle: 315,
        anchor: 'start',
        baseline: 'middle'
      },
    ];

    this.data.config.quadrants.forEach((quadrant) => {
      const position = labelPositions.find(p => p.order === quadrant.order);
      if (!position) return;

      // Convert angle to radians and calculate x, y
      const angleRad = (position.angle * Math.PI) / 180;
      const x = labelDistance * Math.cos(angleRad);
      const y = labelDistance * Math.sin(angleRad);

      labelGroup
        .append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('text-anchor', position.anchor)
        .attr('dominant-baseline', position.baseline)
        .attr('class', 'quadrant-label')
        .attr('data-quadrant-id', quadrant.id)
        .attr('fill', UI_COLORS.quadrantLabel)
        .attr('font-size', `${LABEL_POSITIONING.quadrantLabelFontSize}px`)
        .attr('font-weight', LABEL_POSITIONING.quadrantLabelFontWeight)
        .style('cursor', 'pointer')
        .text(quadrant.name.toUpperCase())
        .on('click', () => {
          if (this.onQuadrantClick) {
            this.onQuadrantClick(quadrant.id);
          }
        })
        .on('mouseenter', function() {
          d3.select(this).attr('fill', '#3b82f6'); // TODO: Add to UI_COLORS
        })
        .on('mouseleave', function() {
          d3.select(this).attr('fill', UI_COLORS.quadrantLabel);
        });
    });
  }

  /**
   * Render blips (technology entries)
   */
  private renderBlips(): void {
    const blipsGroup = this.svg
      .append('g')
      .attr('class', 'blips')
      .attr('transform', `translate(${this.centerX}, ${this.centerY})`);

    this.data.entries.forEach((entry) => {
      const position = this.positions.get(entry.id);
      if (!position) return;

      const ring = this.data.config.rings.find((r) => r.id === entry.ring);
      if (!ring) return;

      this.renderBlip(blipsGroup, entry, position);
    });
  }

  /**
   * Render a single blip - ThoughtWorks style
   */
  private renderBlip(
    group: d3.Selection<SVGGElement, unknown, null, undefined>,
    entry: RadarEntry,
    position: Position
  ): void {
    const { change } = entry;

    // Use centralized color configuration
    const ringColor = getRingColor(entry.ring);

    // Determine blip appearance based on status
    let fillColor: string;
    let strokeColor: string | null = null;
    let strokeWidth = 0;

    if (change.status === 'new') {
      // New items: filled with white center and colored border (like ThoughtWorks)
      fillColor = UI_COLORS.newBadge;
      strokeColor = ringColor;
      strokeWidth = BLIP_STYLING.newStrokeWidth;
    } else if (change.status === 'moved') {
      // Moved items: triangle shape would be better, but using ring color with indicator
      fillColor = ringColor;
      strokeColor = change.direction === 'inward' ? UI_COLORS.moveInward : UI_COLORS.moveOutward;
      strokeWidth = BLIP_STYLING.newStrokeWidth;
    } else {
      // Unchanged: solid filled circle with ring color
      fillColor = ringColor;
    }

    const blipGroup = group
      .append('g')
      .attr('class', 'blip')
      .attr('data-id', entry.id)
      .attr('transform', `translate(${position.x}, ${position.y})`)
      .attr('tabindex', '0')
      .attr('role', 'button')
      .attr('aria-label', entry.title)
      .style('cursor', 'pointer')
      .on('click', () => {
        if (this.onBlipClick) {
          this.onBlipClick(entry);
        }
      })
      .on('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (this.onBlipClick) {
            this.onBlipClick(entry);
          }
        }
      });

    // Main circle
    const circle = blipGroup
      .append('circle')
      .attr('class', 'blip-circle')
      .attr('r', RADAR_DIMENSIONS.blipRadius)
      .attr('fill', fillColor);

    if (strokeColor) {
      circle
        .attr('stroke', strokeColor)
        .attr('stroke-width', strokeWidth);
    }

    // Tooltip
    blipGroup.append('title').text(entry.title);
  }

  /**
   * Render legend - Professional executive style
   */
  private renderLegend(): void {
    const legendGroup = this.svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${LEGEND_CONFIG.x}, ${LEGEND_CONFIG.y})`);

    // Add background rectangle for the legend box
    const legendBg = legendGroup.insert('rect', ':first-child')
      .attr('x', -LEGEND_CONFIG.padding)
      .attr('y', -LEGEND_CONFIG.padding)
      .attr('rx', LEGEND_CONFIG.borderRadius)
      .attr('ry', LEGEND_CONFIG.borderRadius)
      .attr('fill', 'rgba(255, 255, 255, 0.95)')
      .attr('stroke', '#dadce0')
      .attr('stroke-width', 1);

    // Title
    legendGroup
      .append('text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('font-size', `${LEGEND_CONFIG.titleFontSize}px`)
      .attr('font-weight', LEGEND_CONFIG.titleFontWeight)
      .attr('fill', '#2c3e50')
      .text('LEGEND');

    // Example ring color for legend (trial ring)
    const exampleRingColor = getRingColor('trial');

    const legendItems = [
      { label: 'New Technology', fill: UI_COLORS.newBadge, stroke: exampleRingColor, strokeWidth: BLIP_STYLING.newStrokeWidth },
      { label: 'Moving Inward', fill: exampleRingColor, stroke: UI_COLORS.moveInward, strokeWidth: BLIP_STYLING.newStrokeWidth },
      { label: 'Moving Outward', fill: exampleRingColor, stroke: UI_COLORS.moveOutward, strokeWidth: BLIP_STYLING.newStrokeWidth },
      { label: 'Unchanged', fill: exampleRingColor, stroke: null, strokeWidth: 0 },
    ];

    legendItems.forEach((item, index) => {
      const itemGroup = legendGroup
        .append('g')
        .attr('transform', `translate(10, ${(index + 1) * LEGEND_CONFIG.itemHeight + LEGEND_CONFIG.itemSpacing})`);

      const size = 9;

      // Circle
      const circle = itemGroup
        .append('circle')
        .attr('r', size)
        .attr('fill', item.fill);

      if (item.stroke) {
        circle
          .attr('stroke', item.stroke)
          .attr('stroke-width', item.strokeWidth);
      }

      itemGroup
        .append('text')
        .attr('x', LEGEND_CONFIG.itemX)
        .attr('y', 5)
        .attr('font-size', `${LEGEND_CONFIG.itemFontSize}px`)
        .attr('font-weight', '500')
        .attr('fill', '#2c3e50')
        .text(item.label);
    });

    // Ring legend section
    const ringLegendY = (legendItems.length + 1) * LEGEND_CONFIG.itemHeight + 16;
    const ringLegendGroup = legendGroup
      .append('g')
      .attr('transform', `translate(0, ${ringLegendY})`);

    ringLegendGroup
      .append('text')
      .attr('y', 0)
      .attr('font-size', `${LEGEND_CONFIG.titleFontSize}px`)
      .attr('font-weight', LEGEND_CONFIG.titleFontWeight)
      .attr('fill', '#2c3e50')
      .text('RINGS');

    this.data.config.rings.forEach((ring, index) => {
      const ringItem = ringLegendGroup
        .append('g')
        .attr('transform', `translate(0, ${(index + 1) * 26 + LEGEND_CONFIG.itemSpacing})`);

      // Use centralized color configuration
      const ringColor = getRingColor(ring.id);

      ringItem
        .append('circle')
        .attr('cx', 8)
        .attr('cy', 0)
        .attr('r', 8)
        .attr('fill', ringColor)
        .attr('opacity', 1);

      ringItem
        .append('text')
        .attr('x', LEGEND_CONFIG.itemX)
        .attr('y', 5)
        .attr('font-size', `${LEGEND_CONFIG.itemFontSize}px`)
        .attr('font-weight', '500')
        .attr('fill', '#2c3e50')
        .text(ring.name);
    });

    // Calculate legend box dimensions and update background
    const bbox = legendGroup.node()?.getBBox();
    if (bbox) {
      legendBg
        .attr('width', bbox.width + (LEGEND_CONFIG.padding * 2))
        .attr('height', bbox.height + (LEGEND_CONFIG.padding * 2));
    }
  }

  /**
   * Destroy the radar visualization
   */
  destroy(): void {
    this.svg.remove();
  }
}
